import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { ArchivedReport } from '../../../models';
import { DataTable, ReportLabels } from '../../../components/data-table';
import { TableColumn } from '@leasequery/lq.react-component-library/dist/models';
import { DATE_TIME_FORMAT } from '../../../constants';
import {
  TableCell,
  TableConfig,
} from '@leasequery/lq.react-component-library/dist/components/table';

import DeleteButtonCell from './DeleteButtonCell';
import ReportNameCell from './ReportNameCell';
import { useAuth } from '../../../context';
import { escapeRegExp } from '../../../util';

type DeleteButtonClickHandler = (archivedReport: ArchivedReport) => void;

interface ArchiveReportsTableProps {
  reportResults: ArchivedReport[];
  onDeleteButtonClick: DeleteButtonClickHandler;
}

const tableConfig: TableConfig = {
  globalFilterDetector: (item: ArchivedReport, value: string) =>
    new RegExp(escapeRegExp(value), 'i').test(item.name),
};

const createTableColumns = (
  onDeleteButtonClick: DeleteButtonClickHandler,
  isMultiClientUser?: boolean,
): TableColumn[] => {
  const DeleteCell = (cell: TableCell): JSX.Element => (
    <DeleteButtonCell cell={cell} onClick={onDeleteButtonClick} />
  );
  const columns: TableColumn[] = [
    {
      Header: 'Report Name',
      Cell: ReportNameCell,
      accessor: 'name',
    },
    {
      Header: 'Run Date',
      Cell: (cell: TableCell): string => {
        return cell.value
          ? format(cell.value, DATE_TIME_FORMAT.FULL_DATE_TIME_WITH_MONTH_NAME)
          : null;
      },
      accessor: 'runDate',
      width: '19rem',
    },
    {
      Header: 'Delete',
      Cell: DeleteCell,
      accessor: 'taskId',
      width: '8rem',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
  ];

  if (isMultiClientUser) {
    columns.splice(columns.length - 1, 0, {
      Header: 'Client',
      accessor: 'clientName',
    });
  }

  return columns;
};

const labels: ReportLabels = {
  itemCount: 'Report Count:',
  searchPlaceHolder: 'Search Report Results',
};

function ArchiveReportsTable({
  reportResults,
  onDeleteButtonClick,
}: ArchiveReportsTableProps): JSX.Element {
  const { user } = useAuth();
  const columns = useMemo(
    () => createTableColumns(onDeleteButtonClick, user.isMultiClientUser),
    [onDeleteButtonClick, user.isMultiClientUser],
  );

  return (
    <DataTable
      columns={columns}
      data={reportResults}
      config={tableConfig}
      labels={labels}
    />
  );
}

export default ArchiveReportsTable;
